import clsx from 'clsx'
import forEach from 'lodash/forEach'
import reduce from 'lodash/reduce'
import React, {
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { SliderArrow } from '@Components/CMS/ProductSlider/SliderArrow'
import { useHorizontalSlider } from '@Components/CMS/ProductSlider/UseHorizontalSlider'

import styles from './HorizontalSlider.module.scss'

interface Props {
  arrowLeft?: (goToPreviousSlide, disabled?: boolean) => ReactNode
  arrowRight?: (goToNextSlide, disabled?: boolean) => ReactNode
  children?: React.ReactNode
  fullscreen?: boolean
  gap?: number
  itemHeight?: number
  itemWidth: number
  numberOfElements: number
  showArrows?: boolean
  style?: CSSProperties
  useAvgWidth?: { margin: number }
}

/** @deprecated use Sliders from CoreComponents instead */
export const HorizontalSlider: FC<Props> = ({
  arrowLeft,
  arrowRight,
  children,
  fullscreen,
  gap = 0,
  itemHeight = '255px',
  itemWidth,
  numberOfElements,
  showArrows,
  style,
  useAvgWidth,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [calculatedItemWidth, setCalculatedItemWidth] = useState<number>(itemWidth + gap)
  const [calculatedItemsWidth, setCalculatedItemsWidth] = useState<number>(itemWidth + gap)
  const [disableRightArrow, setDisableRightArrow] = useState(false)
  const getStartPost = useCallback(() => {
    if (fullscreen && typeof window !== 'undefined') {
      return ((window.innerWidth - 1200) / 2) - (gap / 2)
    }
    return 0
  }, [fullscreen, gap])
  const [enableDrag, setEnableDrag] = useState(true)

  useEffect(() => {
    if (useAvgWidth && ref?.current?.children.length) {
      const sum = reduce(ref?.current?.children, (acc, child) => {
        const childWidth = Math.ceil(child.clientWidth)
        if (childWidth > 1) {
          return acc + child.clientWidth + gap
        }
        return acc
      }, 0)
      if (sum > 0) {
        setCalculatedItemsWidth(sum)
        setCalculatedItemWidth((sum / ref.current.children.length) + useAvgWidth.margin)
      }
    }
  }, [gap, ref, useAvgWidth])

  const { activeSlide, goToNextSlide, goToPreviousSlide, isDragging } = useHorizontalSlider({
    target: ref,
    itemWidth: calculatedItemWidth,
    itemSize: numberOfElements,
    dragBounds: false,
    disableDrag: !enableDrag,
    startPosition: getStartPost(),
  })

  useEffect(() => {
    if (ref.current) {
      let childrenWidth = 0
      forEach(ref.current.children, (child) => { childrenWidth += child.clientWidth })
      const startPos = getStartPost()
      if (calculatedItemsWidth > ref.current.clientWidth && activeSlide === 0) {
        setDisableRightArrow(false)
      }
      if (childrenWidth <= ref.current.clientWidth || activeSlide === numberOfElements - Math.floor((ref.current.clientWidth - startPos) / itemWidth)) {
        setDisableRightArrow(true)
      } else {
        setDisableRightArrow(false)
      }
    } else {
      setDisableRightArrow(false)
    }
  }, [activeSlide, calculatedItemsWidth, getStartPost, itemWidth, numberOfElements])

  const getShowArrowLeft = () => {
    if (showArrows) {
      if (fullscreen && activeSlide === 0) {
        return false
      }
      return true
    }
    return false
  }

  const disableLeftArrow: boolean = activeSlide === 0

  useEffect(() => {
    if (disableRightArrow && disableLeftArrow) { setEnableDrag(false) }
  }, [disableRightArrow, disableLeftArrow])

  return (
    <>
      {arrowLeft && arrowLeft(goToPreviousSlide, disableLeftArrow)}
      <div className={styles.horizontalSliderWrapper} style={style}>
        {getShowArrowLeft()
        && (
          <SliderArrow
            direction="left"
            disabled={activeSlide === 0}
            onClick={(event) => {
              event.stopPropagation()
              goToPreviousSlide()
            }}
          />
        )}
        <span style={{ paddingTop: `${itemHeight}px`, display: 'block' }} />
        <div
          className={clsx(styles.sliderList, fullscreen && styles.fullwidth)}
          onClick={(event) => {
            if (isDragging.current) {
              event.preventDefault()
            }
          }}
          ref={ref}
          style={{ gap: `${gap || 6}px` } as CSSProperties}
        >
          {children}
        </div>
        {showArrows
        && (
          <SliderArrow
            direction="right"
            disabled={disableRightArrow}
            onClick={(event) => {
              event.stopPropagation()
              goToNextSlide()
            }}
          />
        )}
      </div>

      {arrowRight && arrowRight(goToNextSlide, disableRightArrow)}
    </>
  )
}

